import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

const normalize = (s = "") => s.toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();

export default function FAQPage() {
  const [openIdx, setOpenIdx] = useState(null);
  const [q, setQ] = useState("");

  const FAQ = useMemo(
    () => [
      {
        q: "How do I get matched with a broker?",
        a: (
          <>
            Go to <Link className="link" to="/plans">/plans</Link> and enter your ZIP code. Pick a broker to contact,
            or submit the form and we’ll alert the broker you choose.
          </>
        ),
        cat: "Getting Started",
      },
      {
        q: "What’s the difference between Medicare Advantage and Medigap?",
        a: (
          <>
            Advantage (Part C) replaces Original Medicare and usually bundles extras; Medigap supplements Original
            Medicare to reduce out‑of‑pocket costs. Read our guides: <Link className="link" to="/medicare-advantage">Medicare Advantage</Link>
            {" "}and{" "}
            <Link className="link" to="/medicare-supplements">Medicare Supplement</Link>.
          </>
        ),
        cat: "Medicare",
      },
      {
        q: "Which prescriptions are covered under Part D?",
        a: (
          <>
            Each Part D plan has its own formulary. A broker can run a <strong>drug comparison</strong> to find the best fit
            for your medications. Start at <Link className="link" to="/part-d">Part D</Link>.
          </>
        ),
        cat: "Medicare",
      },
      {
        q: "What documents do agents need for Partner Apply?",
        a: (
          <>
            Upload your <strong>state insurance license</strong> and <strong>E&O certificate</strong> (PDF/JPG/PNG, ≤10MB).
            Apply at <Link className="link" to="/partner-apply">/partner-apply</Link>.
          </>
        ),
        cat: "Partners",
      },
      {
        q: "How quickly will a broker respond?",
        a: (
          <>
            We notify by email (and SMS if enabled). You’ll usually hear back within <strong>1 business day</strong>.
            Urgent?
          </>
        ),
        cat: "Support",
      },
      {
        q: "Is Your Choice Associates an insurance carrier?",
        a: (
          <>
            No—we’re an <strong>independent agency</strong> that compares multiple carriers so you can choose confidently.
          </>
        ),
        cat: "About",
      },
      {
        q: "Do you offer life insurance?",
        a: (
          <>
            Yes. Explore <Link className="link" to="/term-life">Term</Link> and
            {" "}<Link className="link" to="/permanent-life">Permanent</Link> life options, then a broker can quote you.
          </>
        ),
        cat: "Life Insurance",
      },
      {
        q: "I submitted a form but didn’t get a confirmation.",
        a: (
          <>
            Check spam, verify your email, and try again. If nothing arrives after 15 minutes, contact us via the
            <Link className="link" to="/contact"> contact page</Link> and include your full name.
          </>
        ),
        cat: "Support",
      },
    ],
    []
  );

  const results = useMemo(() => {
    if (!q) return FAQ;
    const n = normalize(q);
    return FAQ.filter((f) => normalize(f.q).includes(n) || normalize(String(f.a)).includes(n));
  }, [q, FAQ]);

  return (
    <>
      <Helmet>
        <title>FAQs | Your Choice Associates</title>
        <meta name="description" content="Frequently asked questions about Medicare, life insurance, brokers, and using Your Choice Associates." />
      </Helmet>
      <Navbar />

      <section className="bg-gradient-to-b from-[#f7fbff] to-white">
        <div className="mx-auto max-w-5xl px-6 md:px-8 py-14">
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-block rounded-full bg-[#e8f1fb] px-3 py-1 text-xs font-semibold tracking-wider text-[#002B5C]">FAQs</span>
            <h1 className="mt-4 text-3xl font-extrabold text-[#002B5C] md:text-4xl">Frequently asked questions</h1>
            <p className="mx-auto mt-3 max-w-2xl text-gray-600">Search or browse answers. If you still need help, submit a request and we’ll follow up.</p>
          </motion.div>

          <motion.div {...fadeUp(0.05)} className="mx-auto mt-7 max-w-xl">
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search FAQs…"
                className="w-full rounded-2xl border border-gray-300 bg-white py-3 px-4 shadow-sm focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
              />
            </div>
            {q && (
              <p className="mt-2 text-sm text-gray-600">Showing {results.length} result{results.length !== 1 ? "s" : ""} for “{q}”.</p>
            )}
          </motion.div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {results.map((item, idx) => {
              const open = openIdx === idx;
              return (
                <div key={idx} className="border-b last:border-b-0">
                  <button
                    className="flex w-full items-center justify-between px-5 py-4 text-left focus:outline-none"
                    aria-expanded={open}
                    onClick={() => setOpenIdx(open ? null : idx)}
                  >
                    <span className="font-medium">
                      <span className="mr-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{item.cat}</span>
                      {item.q}
                    </span>
                    <FaChevronDown className={`ml-3 transition ${open ? "rotate-180" : "rotate-0"}`} />
                  </button>
                  <div className={`grid transition-all duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="prose prose-sm max-w-none px-5 pb-5 text-gray-700">{item.a}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link to="/help" className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 font-semibold shadow-sm hover:bg-gray-50">Back to Help Center</Link>
            <Link to="/contact" className="inline-flex items-center rounded-xl bg-[#0B5EFD] px-4 py-2 font-semibold text-white shadow-sm hover:opacity-95">Still need help? Contact us</Link>
          </div>
        </div>
      </section>
      <Footer />

      <style>{`.link{color:#0B5EFD;text-decoration:underline;text-underline-offset:2px}`}</style>
    </>
  );
}
